
interface ServicesSectionProps {
  language: 'es' | 'en';
}

export default function ServicesSection({ language }: ServicesSectionProps) {
  const content = {
    es: {
      title: 'Nuestros Packs & Servicios',
      subtitle: 'Ofrecemos tres paquetes, cada uno diseñado para diferentes necesidades',
      municipal: {
        title: 'MUNICIPAL PACK',
        price: 'USD 250',
        description: 'Perfecto para tus primeros pasos legales en Japón.',
        goal: 'Objetivo: completar todos los trámites municipales en un día.',
        includes: 'Incluye:',
        features: [
          'Registro de residencia',
          'Seguro nacional',
          'Pensión y seguridad social',
          'Acompañamiento presencial y traducción'
        ]
      },
      lifeStart: {
        title: 'LIFE START PACK',
        price: 'USD 425',
        description: 'Ideal para comenzar tu nueva vida en Japón con confianza.',
        goal: 'Todo del Municipal Pack +',
        includes: 'Incluye:',
        features: [
          'Armado de CV japonés personalizado a tu historial',
          'Búsqueda de sharehouses o departamentos confiables',
          'Obtención de sello personal (hanko)',
          'Activación de SIM japonesa',
          'Asistencia para abrir cuenta bancaria japonesa',
          '🎁 Tarjeta de transporte Suica o Icoca incluida (de regalo)'
        ],
        bonus: '🎁 Incluye un pequeño regalo de bienvenida'
      },
      premium: {
        title: 'PREMIUM STEP PACK',
        price: 'USD 600',
        description: 'La experiencia completa, con soporte personalizado total.',
        goal: 'Objetivo: acompañarte desde el aeropuerto hasta la integración completa en Japón.',
        includes: 'Todo de los paquetes anteriores +',
        features: [
          'Asistencia en aeropuerto (Kansai / Haneda / Narita)',
          'Registro en Hello Work (empleo)',
          'Exención del sistema de pensión',
          'Consultas y asesoría 24hs (WhatsApp o LINE)'
        ]
      },
      selectPack: 'Seleccionar Pack'
    },
    en: {
      title: 'Our Packs & Services',
      subtitle: 'We offer three packages, each designed for different needs',
      municipal: {
        title: 'MUNICIPAL PACK',
        price: 'USD 250',
        description: 'Perfect for your first legal steps in Japan.',
        goal: 'Goal: complete all municipal paperwork in one day.',
        includes: 'Includes:',
        features: [
          'Residence registration',
          'National insurance',
          'Pension and social security',
          'In-person support and translation'
        ]
      },
      lifeStart: {
        title: 'LIFE START PACK',
        price: 'USD 425',
        description: 'Ideal for beginning your new life in Japan with confidence.',
        goal: 'Everything from Municipal Pack +',
        includes: 'Includes:',
        features: [
          'Personalized Japanese CV creation',
          'Search for reliable sharehouses or apartments',
          'Personal seal (hanko) acquisition',
          'Japanese SIM activation',
          'Assistance opening a Japanese bank account',
          '🎁 Includes a complimentary Suica or Icoca transportation card'
        ],
        bonus: '🎁 Includes a small welcome gift'
      },
      premium: {
        title: 'PREMIUM STEP PACK',
        price: 'USD 600',
        description: 'The complete experience, with full personalized support.',
        goal: 'Goal: accompany you from the airport to full integration in Japan.',
        includes: 'Everything from previous packs +',
        features: [
          'Airport assistance (Kansai / Haneda / Narita)',
          'Hello Work registration (employment)',
          'Pension system exemption',
          '24h consultation and advice (WhatsApp or LINE)'
        ]
      },
      selectPack: 'Select Pack'
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4">
              {content[language].title} 💼
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content[language].subtitle}
            </p>
          </div>

          {/* Service Cards */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Municipal Pack */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 border border-red-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                  🏯
                </div>
                <h3 className="text-2xl font-bold text-red-600 mb-2">
                  {content[language].municipal.title} 🇯🇵
                </h3>
                <div className="text-3xl font-bold text-red-700 mb-4">
                  {content[language].municipal.price}
                </div>
                <p className="text-gray-700 mb-4">
                  {content[language].municipal.description}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <h4 className="font-semibold text-red-600">{content[language].municipal.includes}</h4>
                <ul className="space-y-2">
                  {content[language].municipal.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <i className="ri-check-line text-red-600 mt-1"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-red-600 font-medium bg-red-50 p-3 rounded-lg">
                  {content[language].municipal.goal}
                </p>
              </div>

              <button
                onClick={scrollToContact}
                className="w-full bg-red-600 hover:bg-red-7

                text-white font-bold py-3 px-6 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                {content[language].selectPack}
              </button>
            </div>

            {/* Life Start Pack */}
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 border-2 border-yellow-400 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-red-900 px-4 py-2 rounded-full text-sm font-bold">
                POPULAR
              </div>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-yellow-500 rounded

                flex items-center justify-center mx-auto mb-4 text-2xl">
                  🌸
                </div>
                <h3 className="text-2xl font-bold text-yellow-700 mb-2">
                  {content[language].lifeStart.title} 🇯🇵
                </h3>
                <div className="text-3xl font-bold text-yellow-800 mb-4">
                  {content[language].lifeStart.price}
                </div>
                <p className="text-gray-700 mb-4">
                  {content[language].lifeStart.description}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <h4 className="font-semibold text-yellow-700">{content[language].lifeStart.goal}</h4>
                <ul className="space-y-2">
                  {content[language].lifeStart.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <i className="ri-check-line text-yellow-600 mt-1"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-yellow-700 font-medium bg-yellow-50 p-3 rounded-lg">
                  {content[language].lifeStart.bonus}
                </p>
              </div>

              <button
                onClick={scrollToContact}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-red-900 font-bold py-3 px-6 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                {content[language].selectPack}
              </button>
            </div>

            {/* Premium Pack */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 border border-gray-300">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                  💎
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  {content[language].premium.title} 🇯🇵
                </h3>
                <div className="text-3xl font-bold text-gray-900 mb-4">
                  {content[language].premium.price}
                </div>
                <p className="text-gray-700 mb-4">
                  {content[language].premium.description}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <h4 className="font-semibold text-gray-800">{content[language].premium.includes}</h4>
                <ul className="space-y-2">
                  {content[language].premium.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <i className="ri-check-line text-gray-600 mt-1"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-700 font-medium bg-gray-50 p-3 rounded-lg">
                  {content[language].premium.goal}
                </p>
              </div>

              <button
                onClick={scrollToContact}
                className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                {content[language].selectPack}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
